import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Image as ImgIcon, Video, Sparkles, Facebook, Instagram, Clock, CheckCircle2, XCircle, ExternalLink, Trash2, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { PublishDialog } from '@/components/PublishDialog';
import { useAuth } from '@/contexts/AuthContext';

const STATUS = {
  published: { label: 'Published', cls: 'bg-boosta-emerald/15 text-boosta-emerald border-boosta-emerald/30', icon: CheckCircle2 },
  scheduled: { label: 'Scheduled', cls: 'bg-boosta-cyan/15 text-boosta-cyan border-boosta-cyan/30', icon: Clock },
  publishing: { label: 'Publishing', cls: 'bg-boosta-yellow/15 text-boosta-yellow border-boosta-yellow/30', icon: Clock },
  failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-400 border-red-500/30', icon: XCircle },
};

export default function Composer() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [posts, setPosts] = useState([]);
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState({});
  const [insightBusy, setInsightBusy] = useState(null);

  const loadInsights = async (id) => {
    setInsightBusy(id);
    try {
      const { data } = await api.get(`/social/posts/${id}/insights`);
      setInsights(prev => ({ ...prev, [id]: data.metrics }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInsightBusy(null); }
  };

  const loadPosts = async () => {
    try { const { data } = await api.get('/social/posts'); setPosts(data); } catch (e) { console.error('Load posts failed', e); }
  };
  const load = async () => {
    try { const { data } = await api.get('/creatives'); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadPosts(); }, []);

  // Auto-refresh while a post is publishing or a scheduled post is due, so its
  // status flips to Published/Failed without a manual page reload.
  const needsPoll = posts.some(p =>
    p.status === 'publishing' ||
    (p.status === 'scheduled' && new Date(p.scheduled_at) <= new Date(Date.now() + 60000)));
  useEffect(() => {
    if (!needsPoll) return;
    const t = setInterval(loadPosts, 12000);
    return () => clearInterval(t);
  }, [needsPoll]);

  const cancelPost = async (id) => {
    if (!window.confirm('Remove this post from history?')) return;
    try { await api.delete(`/social/posts/${id}`); toast.success('Removed'); await loadPosts(); }
    catch (e) { toast.error('Could not remove'); }
  };

  return (
    <div className="space-y-8" data-testid="composer-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Publisher</div>
        <h1 className="font-display font-black text-4xl tracking-tighter text-3d">Post creatives to social</h1>
        <p className="text-white/55 text-sm mt-1">Pick a creative, generate an AI caption, and publish now or schedule to Facebook &amp; Instagram.</p>
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-6 animate-pulse">
          <div className="h-4 w-40 bg-white/10 rounded mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={`sk-${i}`} className="h-36 bg-white/[0.05] rounded-xl" />)}
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl py-16 text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-3 text-white/20"/>
          <div className="text-white/55 text-sm mb-4">No creatives yet — generate one first.</div>
          <Link to="/creatives" className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"><Sparkles className="w-4 h-4"/> Go to Creative Studio</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(c => (
            <button key={c.creative_id} onClick={()=>setTarget(c)} disabled={c.type==='video' && c.status!=='ready'}
              className="glass glass-hover rounded-2xl overflow-hidden group text-left disabled:opacity-50" data-testid={`composer-pick-${c.creative_id}`}>
              <div className="aspect-square bg-black/40 relative overflow-hidden flex items-center justify-center">
                {c.type === 'video' ? (
                  c.status === 'ready' && c.video_url
                    ? <video src={c.video_url} className="w-full h-full object-cover" muted/>
                    : <div className="text-[11px] text-boosta-cyan">Rendering…</div>
                ) : <img src={c.image_url} alt="" className="w-full h-full object-cover transition group-hover:scale-105"/>}
                <span className="absolute top-2 left-2 text-[9px] px-2 py-0.5 rounded-full bg-black/50 text-white/80 uppercase tracking-wider inline-flex items-center gap-1">
                  {c.type === 'video' ? <Video className="w-2.5 h-2.5"/> : <ImgIcon className="w-2.5 h-2.5"/>}{c.type}
                </span>
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition">
                  <span className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1"><Send className="w-3 h-3"/> Post</span>
                </span>
              </div>
              <div className="p-3"><div className="text-xs text-white/65 line-clamp-2">{c.prompt}</div></div>
            </button>
          ))}
        </div>
      )}

      <div data-testid="posts-history">
        <div className="font-display font-bold text-xl mb-4">Published &amp; scheduled posts</div>
        {posts.length === 0 ? (
          <div className="glass rounded-2xl py-12 text-center text-white/50 text-sm">No posts yet — publish or schedule your first creative above.</div>
        ) : (
          <div className="glass rounded-2xl divide-y divide-white/5">
            {posts.map(p => {
              const s = STATUS[p.status] || STATUS.failed;
              const SIcon = s.icon;
              const when = p.status === 'scheduled' ? p.scheduled_at : p.published_at || p.created_at;
              const m = insights[p.post_id];
              return (
                <div key={p.post_id} className="px-5 py-4" data-testid={`post-${p.post_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                      {p.platform === 'facebook' ? <Facebook className="w-4 h-4 text-boosta-cyan"/> : <Instagram className="w-4 h-4 text-boosta-cyan"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/80 truncate">{p.caption}</div>
                      <div className="text-[11px] text-white/40 mt-0.5 flex items-center gap-2">
                        <span>{p.page_name || p.page_id}</span>·<span>{when ? new Date(when).toLocaleString() : '—'}</span>
                        {p.status === 'failed' && p.error && <span className="text-red-400/80 truncate">· {p.error}</span>}
                      </div>
                    </div>
                    <span className={`text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider font-bold border inline-flex items-center gap-1 ${s.cls}`}>
                      <SIcon className="w-3 h-3"/>{s.label}
                    </span>
                    {p.status === 'published' && user?.features?.insights && (
                      <button onClick={()=>loadInsights(p.post_id)} disabled={insightBusy===p.post_id} className="text-white/50 hover:text-boosta-cyan disabled:opacity-40" title="View insights" data-testid={`post-insights-${p.post_id}`}>
                        <BarChart3 className={`w-4 h-4 ${insightBusy===p.post_id?'animate-pulse':''}`}/>
                      </button>
                    )}
                    {p.permalink && <a href={p.permalink} target="_blank" rel="noreferrer" className="text-white/50 hover:text-boosta-cyan" title="View live post" data-testid={`post-link-${p.post_id}`}><ExternalLink className="w-4 h-4"/></a>}
                    <button onClick={()=>cancelPost(p.post_id)} className="text-white/40 hover:text-red-400" title="Remove" data-testid={`post-delete-${p.post_id}`}><Trash2 className="w-4 h-4"/></button>
                  </div>
                  {m && (
                    <div className="flex gap-5 mt-3 pl-12 text-xs" data-testid={`post-metrics-${p.post_id}`}>
                      {[['Impressions','impressions'],['Reach','reach'],['Likes','likes'],['Comments','comments'],['Shares','shares']].map(([label,key]) => (
                        m[key] != null && <div key={key}><span className="font-display font-bold text-boosta-cyan text-base">{m[key]}</span> <span className="text-white/45">{label}</span></div>
                      ))}
                      {Object.values(m).every(v => v == null) && <span className="text-white/40">No insights available yet.</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {target && <PublishDialog creative={target} onClose={()=>setTarget(null)} onPublished={loadPosts} />}
    </div>
  );
}
